<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { useAuthStore } from '@/stores/authStore';
import { flushPropertyStoreV2IfActive } from '@/stores/propertyStoreV2';
import { flushPropertyStoreIfActive } from '@/stores/propertyStore';
import { flushSmokingStoreIfActive } from '@/stores/smokingStore';
import { flushKnowledgeStoreIfActive } from '@/stores/knowledgeStore';
import { flushBuildingStoreIfActive } from '@/stores/buildingStore';
import BuildingMasterLinkDialog from '@/components/common/BuildingMasterLinkDialog.vue';

const router = useRouter();
const auth = useAuthStore();
const { width } = useDisplay();

/**
 * ヘッダーを2段にする幅 (px)。
 *
 * エディタは主アクションが4つ ([地図を合わせる][画像出力][削除][保存]) あり、
 * これより狭いとブランド名の右にパンくずを置く余地が無く、文字の途中で切れる。
 * 隠すとページ階層が分からなくなるので、ブランド名の下へ落とす。
 */
const HEADER_STACK_W = 1100;
const stacked = computed(() => width.value <= HEADER_STACK_W);
/** 2段のときのヘッダー高さ (px)。v-main の位置もこの値から決まる。 */
const HEADER_H_STACKED = 84;
// 物件マスタの取り込みは画面に出さない (管理用ページ /admin/building-master から行う)。
const linkDialog = ref(false);

function goHome() {
  router.push({ name: 'Home' });
}

/**
 * ログアウト前に未送信の変更を flush する (§4.2 の離脱時セーフティネットと
 * 同様の理由 — signOutUser 後は権限が失われ、以後の flush は permission-denied
 * になり得るため、サインアウト自体より先に完了させる)。
 * flushXxxIfActive は ストア未生成の場合に Firestore 初期化を避け、signOut との
 * 競合を防ぐ。4ストア (v1/v2/喫煙所/ナレッジ) は独立なので並行に flush する。
 * flush 後は明示的に Login へ遷移させる。
 */
async function logout(): Promise<void> {
  await Promise.all([
    flushPropertyStoreIfActive(),
    flushPropertyStoreV2IfActive(),
    flushSmokingStoreIfActive(),
    flushKnowledgeStoreIfActive(),
    flushBuildingStoreIfActive(),
  ]);
  await auth.signOutUser();
  router.push({ name: 'Login' });
}
</script>

<template>
  <v-app-bar
    flat
    density="comfortable"
    color="surface"
    class="app-header"
    :class="{ 'app-header--stacked': stacked }"
    :height="stacked ? HEADER_H_STACKED : undefined"
  >
    <div class="header-lead">
      <!-- クリック領域はロゴ文字列のみ (v-app-bar-title 全体だとヘッダー左半分が
         丸ごとクリック可能になり誤タップでホームに飛ぶため) -->
      <v-app-bar-title class="header-title">
        <span
          class="brand"
          role="link"
          tabindex="0"
          @click="goHome"
          @keydown.enter="goHome"
        >GRAND Map Maker</span>
      </v-app-bar-title>

      <!--
        パンくずの差し込み口。各ページの <Breadcrumbs> が Teleport でここに入る。
        ページ本文ではなくヘッダーに置くことで、どの画面でも同じ位置に出る。
        幅が足りないときは .header-lead が縦並びになり、ブランド名の下に落ちる。
      -->
      <div
        id="app-header-breadcrumbs"
        class="header-crumbs"
      />
    </div>

    <v-spacer />

    <!--
      エディタページ (避難マップ / 災害時避難先 / 喫煙所) が主アクション
      [地図を合わせる][画像出力][保存] を Teleport でここへ差し込む。
      非エディタページでは空 (ブランド + アカウントのみ)。
    -->
    <div
      id="editor-header-actions"
      class="header-actions"
    />

    <v-menu v-if="auth.status === 'ready' && auth.email !== 'local@dev'">
      <template #activator="{ props: menuProps }">
        <v-btn
          v-bind="menuProps"
          variant="text"
          icon="mdi-account-circle"
          :aria-label="auth.email"
          :title="auth.email"
        />
      </template>
      <v-list density="compact">
        <v-list-item
          title="既存の物件をマスタに紐付ける"
          prepend-icon="mdi-link-variant"
          @click="linkDialog = true"
        />
        <v-divider />
        <v-list-item
          title="ログアウト"
          @click="logout"
        />
      </v-list>
    </v-menu>
    <!-- ローカルモードでも紐付けは使えるようにする (動作確認用。右クリック) -->
    <button
      v-else-if="auth.email === 'local@dev'"
      type="button"
      class="login-chip login-chip--action"
      title="右クリックで既存の物件をマスタに紐付け"
      @contextmenu.prevent="linkDialog = true"
    >
      ローカル
    </button>
    <span
      v-else
      class="login-chip"
    >未ログイン</span>

    <BuildingMasterLinkDialog v-model:open="linkDialog" />
  </v-app-bar>
</template>

<style scoped>
.app-header {
  border-bottom: 1px solid var(--ds-border-subtle);
}

/* ブランド名とパンくずのまとまり。広いときは横並び、狭いときは縦並び。 */
.header-lead {
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: var(--ds-space-md);
  /* Vuetify の左余白は `.v-toolbar__content > .v-toolbar-title` という
     直下の子への指定なので、ここで包むと効かなくなる。同じ値で補う。 */
  margin-inline-start: 16px;
}

.app-header--stacked .header-lead {
  flex-direction: column;
  align-items: flex-start;
  gap: var(--ds-space-3xs);
}

.header-title {
  /* 内容幅で固定し伸縮させない (既定の flex:1 だと v-spacer と伸長を
     分け合ってブランド名が省略される)。余白は v-spacer が吸収する。 */
  flex: 0 0 auto;
  display: flex;
  align-items: baseline;
  gap: var(--ds-space-md);
}

.brand {
  cursor: pointer;
  font-size: var(--ds-font-lg);
  font-weight: var(--ds-font-bold);
  color: var(--ds-text-primary);
  letter-spacing: 0.02em;
}

/* ページが Teleport で差し込むパンくずの器。 */
.header-crumbs {
  display: flex;
  align-items: center;
  min-width: 0;
}

/*
 * ページ側はもともと本文用の余白を付けている (margin-bottom: 16px など)。
 * ヘッダーでは不要なので打ち消す。折り返すとヘッダーの高さが変わるので
 * 1行に固定し、入りきらない場合は省略記号にする。
 */
.header-crumbs :deep(nav.breadcrumbs) {
  margin: 0;
  padding: 0;
  min-height: 0;
  /* min-width:0 が無いと内容幅を下回れず、狭いときに主アクションを
     ヘッダーの外へ押し出してしまう。 */
  min-width: 0;
  flex-wrap: nowrap;
  white-space: nowrap;
  overflow: hidden;
}

/*
 * 2段のときはブランド名の下に来るので、主アクションと幅を取り合わない。
 * 行の高さぶんだけ詰めて、ヘッダーが間延びしないようにする。
 */
.app-header--stacked .header-crumbs :deep(nav.breadcrumbs) {
  font-size: var(--ds-font-xs, 12px);
}

/* エディタが Teleport で差し込む主アクションの器。 */
.header-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  margin-right: var(--ds-space-sm);
}

.login-chip {
  font-size: var(--ds-font-sm);
  padding: 4px 12px;
  border-radius: var(--ds-radius-full);
  background: var(--ds-bg-hover);
  color: var(--ds-text-secondary);
}

/* ローカルモードの chip を取込ダイアログの起動口にする。 */
.login-chip--action {
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: var(--ds-font-sm);
}

.login-chip--action:hover {
  color: var(--ds-text-primary);
}
</style>
